<template>
    <div>
        <BufferDialog
            :is-open="bufferDialog"
            :selected-products="buffer"
            @remove-from-buffer="removeFromBuffer"
            @on-close="bufferDialog = false"
        />
        <v-dialog
            :value="isOpen"
            scrollable
            max-width="950px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <v-row class="d-flex justify-space-between">
                        <v-col cols="4">
                            <h1 class="text-subtitle-1">
                                <template>
                                    Utworzenie z kampanii
                                </template>
                            </h1>
                        </v-col>
                        <v-spacer />
                        <v-col cols="12" sm="4" class="justify-end">
                            <v-select
                                v-model="selectedDeliveryMethod"
                                :items="deliveryMethods"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono środka transportu"
                                label="Środek transportu"
                                outlined
                                dense
                            />
                        </v-col>   
                        <v-col cols="auto" class="d-flex justify-end mt-1">
                            <v-chip
                                link
                                color="primary"
                                @click="openBufferDialog"
                            >
                                Dodano: {{ buffer.length }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-card-title>

                <v-card-text class="text-center">
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="filters.campaignNumber"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('campaignNumber')"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-data-table
                                    :headers="headers"
                                    :items="products"
                                    :page.sync="pagination.page"
                                    :footer-props="footerProps"
                                    :mobile-breakpoint="0"
                                    name="shippingOrdersCampaign"
                                    item-key="id"
                                    @pagination="updatePagination"
                                >
                                    <template #item="{item}">
                                        <tr :class="{'isSelected': isProductAlreadyAdded(item)}">
                                            <td>
                                                {{ item.orderProductionNumber }}
                                            </td>
                                            <td>
                                                {{ item.name }}
                                            </td>
                                            <td>
                                                {{ item.productType.name }}
                                            </td>
                                            <td>
                                                {{ formatDate(item.plannedSendDate) }}
                                            </td>
                                            <td>
                                                {{ item.amount }}
                                            </td>
                                            <td>
                                                <div class="d-inline-flex">
                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                v-if="!isProductAlreadyAdded(item)"
                                                                icon
                                                                data-sentry-label="Dodaj do Bufora"
                                                                v-on="on"
                                                                @click="addToBuffer(item)"
                                                            >
                                                                <v-icon color="blue">
                                                                    mdi-plus-box-multiple
                                                                </v-icon>
                                                            </v-btn>
                                                            <v-btn
                                                                v-else
                                                                icon
                                                                data-sentry-label="Usuń z bufora"
                                                                v-on="on"
                                                                @click="removeFromBuffer(item)"
                                                            >
                                                                <v-icon color="red">
                                                                    mdi-close-box-multiple
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span v-if="!isProductAlreadyAdded(item)">Dodaj do schowka</span>
                                                        <span v-else>Usuń ze schowka</span>
                                                    </v-tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                    <template #no-data>
                                        Nie znaleziono zamówień
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        :loading="actionLoading"
                        @click="createShipmentOrderFromOrders"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import BufferDialog from './BufferDialog'

export default {
    name: 'CreateShipmentOrderByCampaignDialog',
    components: {
        BufferDialog
    },
    mixins: [
        pagination,
        filters,
        dateFormatter,
        tooltipDelay,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        deliveryMethods: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            actionLoading: false,
            products: [],
            buffer: [],
            bufferDialog: false,
            selectedDeliveryMethod: null,
            headers: [
                {
                    text: 'Zamówienie',
                    value: 'orderProductionNumber',
                    sortable: false,
                },
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false,
                },
                {
                    text: 'Typ produktu',
                    value: 'productType',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Data wysyłki',
                    value: 'plannedSendDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center'
                }
            ]
        }
    },
    computed: {
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] !== null && typeof filters[item] !== 'undefined') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    methods: {
        getOrders() {
            this.actionLoading = true
            this.$store.dispatch('orders/getOrdersProduct', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                    this.products = response.data.results
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        createShipmentOrderFromOrders() {
            this.$emit(
                'create-shipments-order-from-orders',
                {
                    orderProducts: this.buffer.map(product => ({
                        orderProductId: product.id,
                        amount: product.amount
                    })),
                    deliveryMethodId: this.selectedDeliveryMethod
                }
            )
        },
        changeFilter() {
            if (!this.filters.campaignNumber || this.filters.campaignNumber.length < 3) {
                return
            }

            this.resetPagination()
            this.getOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination

            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getOrders()
            }
            this.clearPaginationPending()
        },
        openBufferDialog() {
            this.bufferDialog = true
        },
        addToBuffer(item) {
            item._campaign = this.filters.campaignNumber
            this.buffer.push(item)
        },
        removeFromBuffer(item) {
            let index = this.buffer.findIndex(product => product.id === item.id)

            this.buffer.splice(index, 1)
        },
        isProductAlreadyAdded(product) {
            return this.buffer.some(item => item.id === product.id)
        },
        onClose() {
            this.$emit('on-close')
        }
    }
}
</script>
